'use client';

import type { getNtProductPriceDisplay } from '@eliadmin/core/src/utils/nt/product-price';
import { WholesalePrices } from '@eliadmin/core/src/components/nt/product/product-price';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { fillTemplate } from '@eliadmin/core/src/utils/nt/text';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type PriceDisplay = ReturnType<typeof getNtProductPriceDisplay>;

/**
 * Figma „Price block“ (112:1289): Price/L (text/danger when discounted), the price before discount
 * struck through, the red chip „Ušetríte {suma}“ and the caption „s DPH · {suma} bez DPH“. The admin
 * texts name the price with VAT first, so the block always leads with it.
 */
export function HeroPrice({
  price,
  youSave,
  withoutVat,
}: {
  price: PriceDisplay;
  /** „Ušetríte {suma}“. */
  youSave: string;
  /** „s DPH · {suma} bez DPH“; '' hides the caption (store setting `show_price_without_vat`). */
  withoutVat: string;
}) {
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const format = (value: number | undefined) => formatPrice(value, currency, currencySettings);

  if (price.showMocVocPrices)
    return <WholesalePrices price={price} detail showSecondary={!!withoutVat} />;

  const current = price.currentWithVat;
  const before = price.beforeDiscountWithVat;
  const discounted =
    price.showPreviousPrice && current != null && before != null && before > current;

  return (
    <div className="flex flex-col gap-1">
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span
          className={cn(
            'nt-price-l whitespace-nowrap',
            discounted ? 'text-[#B42318]' : 'text-[#0D110D]',
          )}
        >
          {format(current)}
        </span>
        {discounted ? (
          <>
            <del className="nt-price-old whitespace-nowrap text-[#5D655D]">{format(before)}</del>
            {youSave ? (
              <span className="nt-body-xs whitespace-nowrap rounded-[8px] bg-[#D92D20] px-2 py-1.5 text-white">
                {fillTemplate(youSave, { suma: format(before - current) })}
              </span>
            ) : null}
          </>
        ) : null}
      </p>
      {withoutVat && price.currentWithoutVat != null ? (
        <p className="nt-caption text-[#5D655D]">
          {fillTemplate(withoutVat, { suma: format(price.currentWithoutVat) })}
        </p>
      ) : null}
    </div>
  );
}
